import { HttpErrorResponse } from '@angular/common/http';

/**
 * Contract of the in-app activation (tenant API, Company Administrators only):
 *
 * - GET  api/v1/tenant/activation               what the modal offers
 * - POST api/v1/tenant/activation/setup-intent  client secret for a new card
 * - POST api/v1/tenant/activation/confirm       books the chosen price
 * - POST api/v1/tenant/activation/confirm-saved books it with the payment method saved at signup
 *
 * Every parser here is strict: an answer that does not match the contract is
 * treated like an unavailable activation, so the old link flow takes over.
 */

/** `checkout`: the modal. `contact`: book a call with sales. `unavailable`: use the activation link. */
export type ActivationMode = 'checkout' | 'contact' | 'unavailable';
export type ActivationPlan = 'small' | 'medium' | 'large';
export type ActivationInterval = 'month' | 'year';
export type ActivationCurrency = 'eur' | 'usd';

export const ACTIVATION_PLANS: readonly ActivationPlan[] = ['small', 'medium', 'large'];
export const ACTIVATION_INTERVALS: readonly ActivationInterval[] = ['month', 'year'];

/** One plan, interval and RSS combination. Exactly one price exists per combination. */
export interface ActivationSelection {
  plan: ActivationPlan;
  interval: ActivationInterval;
  rss: boolean;
}

export interface ActivationOption extends ActivationSelection {
  priceId: string;
  /** Minor units (cents) per interval. */
  amount: number;
  currency: ActivationCurrency;
}

export interface ActivationCurrent extends ActivationSelection {
  priceId: string;
}

export type ActivationPaymentMethodType = 'card' | 'sepa_debit' | 'us_bank_account';

/**
 * The payment method saved on the Stripe customer (at signup or by an earlier
 * activation). Cards carry brand and expiry, SEPA debits the IBAN's country,
 * US bank accounts the bank name. `id` is null in answers of an older master.
 */
export interface ActivationPaymentMethod {
  id: string | null;
  type: ActivationPaymentMethodType;
  last4: string;
  brand: string | null;
  expMonth: number | null;
  expYear: number | null;
  bankName: string | null;
  country: string | null;
}

export interface ActivationCheckout {
  mode: ActivationMode;
  contactUrl: string | null;
  publishableKey: string | null;
  currency: ActivationCurrency;
  /** Stripe subscription status: trialing, paused, active, past_due, ... */
  status: string;
  trialEndsAt: string | null;
  paymentMethod: ActivationPaymentMethod | null;
  current: ActivationCurrent | null;
  options: ActivationOption[];
}

export type ActivationConfirmOutcome = 'activated' | 'scheduled' | 'payment_failed';

export interface ActivationConfirmResult {
  result: ActivationConfirmOutcome;
  status: string;
  invoiceUrl: string | null;
}

/** 409 reasons of POST api/v1/tenant/activation/confirm and confirm-saved. */
export type ActivationConflictReason =
  | 'workspace_gone'
  | 'subscription_ended'
  | 'setup_incomplete'
  | 'price_unavailable'
  | 'payment_method_unavailable';

const CONFLICT_REASONS: readonly ActivationConflictReason[] = [
  'workspace_gone',
  'subscription_ended',
  'setup_incomplete',
  'price_unavailable',
  'payment_method_unavailable',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPlan(value: unknown): value is ActivationPlan {
  return ACTIVATION_PLANS.includes(value as ActivationPlan);
}

function isInterval(value: unknown): value is ActivationInterval {
  return ACTIVATION_INTERVALS.includes(value as ActivationInterval);
}

function isCurrency(value: unknown): value is ActivationCurrency {
  return value === 'eur' || value === 'usd';
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Absolute http(s) or mailto links only; anything else could run script in our origin. */
export function safeExternalUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  try {
    const protocol = new URL(value).protocol;
    return protocol === 'https:' || protocol === 'http:' || protocol === 'mailto:' ? value : null;
  } catch {
    return null;
  }
}

function parseSelection(value: Record<string, unknown>): ActivationSelection | null {
  const { plan, interval, rss } = value;
  return isPlan(plan) && isInterval(interval) && typeof rss === 'boolean' ? { plan, interval, rss } : null;
}

function parseOption(value: unknown, fallbackCurrency: ActivationCurrency): ActivationOption | null {
  if (!isRecord(value)) {
    return null;
  }
  const selection = parseSelection(value);
  const priceId = nonEmptyString(value['priceId']);
  const amount = value['amount'];
  if (selection === null || priceId === null || typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) {
    return null;
  }
  const currency = isCurrency(value['currency']) ? value['currency'] : fallbackCurrency;
  return { ...selection, priceId, amount, currency };
}

function parseCurrent(value: unknown): ActivationCurrent | null {
  if (!isRecord(value)) {
    return null;
  }
  const selection = parseSelection(value);
  const priceId = nonEmptyString(value['priceId']);
  return selection === null || priceId === null ? null : { ...selection, priceId };
}

function optionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parsePaymentMethod(value: unknown): ActivationPaymentMethod | null {
  if (!isRecord(value)) {
    return null;
  }
  const last4 = nonEmptyString(value['last4']);
  // An older master only sent cards, without id and type.
  const type = value['type'] ?? 'card';
  if (last4 === null || (type !== 'card' && type !== 'sepa_debit' && type !== 'us_bank_account')) {
    return null;
  }
  const method: ActivationPaymentMethod = {
    id: nonEmptyString(value['id']),
    type,
    last4,
    brand: nonEmptyString(value['brand']),
    expMonth: optionalNumber(value['expMonth']),
    expYear: optionalNumber(value['expYear']),
    bankName: nonEmptyString(value['bankName']),
    country: nonEmptyString(value['country']),
  };
  return type === 'card' && method.brand === null ? null : method;
}

/**
 * Reads GET api/v1/tenant/activation. Returns null for anything the modal
 * cannot work with, including a checkout without publishable key or prices
 * and a contact mode without a safe link.
 */
export function parseActivationCheckout(data: unknown): ActivationCheckout | null {
  if (!isRecord(data)) {
    return null;
  }
  const mode = data['mode'];
  if (mode !== 'checkout' && mode !== 'contact' && mode !== 'unavailable') {
    return null;
  }

  const currency: ActivationCurrency = isCurrency(data['currency']) ? data['currency'] : 'eur';
  const options = Array.isArray(data['options'])
    ? data['options']
        .map((option) => parseOption(option, currency))
        .filter((option): option is ActivationOption => option !== null)
    : [];
  const publishableKey = nonEmptyString(data['publishableKey']);
  const contactUrl = safeExternalUrl(data['contactUrl']);

  if (mode === 'checkout' && (publishableKey === null || !publishableKey.startsWith('pk_') || options.length === 0)) {
    return null;
  }
  if (mode === 'contact' && contactUrl === null) {
    return null;
  }

  return {
    mode,
    contactUrl,
    publishableKey,
    currency,
    status: typeof data['status'] === 'string' ? data['status'] : '',
    trialEndsAt: nonEmptyString(data['trialEndsAt']),
    paymentMethod: parsePaymentMethod(data['paymentMethod']),
    current: parseCurrent(data['current']),
    options,
  };
}

/** Reads POST api/v1/tenant/activation/setup-intent. */
export function parseSetupIntentSecret(data: unknown): string | null {
  return isRecord(data) ? nonEmptyString(data['clientSecret']) : null;
}

/** Reads POST api/v1/tenant/activation/confirm. */
export function parseActivationConfirmResult(data: unknown): ActivationConfirmResult | null {
  if (!isRecord(data)) {
    return null;
  }
  const result = data['result'];
  if (result !== 'activated' && result !== 'scheduled' && result !== 'payment_failed') {
    return null;
  }
  return {
    result,
    status: typeof data['status'] === 'string' ? data['status'] : '',
    invoiceUrl: safeExternalUrl(data['invoiceUrl']),
  };
}

/**
 * The reason of a 409 from the confirm endpoint. The body may be the plain
 * `{ reason }` object, the API envelope around it, or its JSON text.
 */
export function activationConflictReason(error: unknown): ActivationConflictReason | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return null;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(body)) {
    return null;
  }
  const candidates: unknown[] = [body['reason'], isRecord(body['data']) ? body['data']['reason'] : undefined];
  return (
    (candidates.find((candidate) => CONFLICT_REASONS.includes(candidate as ActivationConflictReason)) as
      | ActivationConflictReason
      | undefined) ?? null
  );
}

/** The price for one combination, if the workspace can book it. */
export function findActivationOption(
  options: readonly ActivationOption[],
  selection: ActivationSelection,
): ActivationOption | null {
  return (
    options.find(
      (option) =>
        option.plan === selection.plan && option.interval === selection.interval && option.rss === selection.rss,
    ) ?? null
  );
}

/** Starts from the booked plan; without one (or when it is no longer offered) from the first price. */
export function initialActivationSelection(checkout: ActivationCheckout): ActivationSelection {
  const current = checkout.current;
  if (current !== null && findActivationOption(checkout.options, current) !== null) {
    return { plan: current.plan, interval: current.interval, rss: current.rss };
  }
  // Checkout answers without options are rejected by parseActivationCheckout.
  const first = checkout.options[0] ?? { plan: 'medium', interval: 'month', rss: false };
  return { plan: first.plan, interval: first.interval, rss: first.rss };
}

/** Which choices lead to a bookable price from the current selection. Disabled ones have none. */
export interface ActivationAvailability {
  plans: Record<ActivationPlan, boolean>;
  intervals: Record<ActivationInterval, boolean>;
  /** Whether the RSS checkbox can be toggled. */
  rssToggle: boolean;
}

export function activationAvailability(
  options: readonly ActivationOption[],
  selection: ActivationSelection,
): ActivationAvailability {
  const has = (change: Partial<ActivationSelection>): boolean =>
    findActivationOption(options, { ...selection, ...change }) !== null;
  return {
    plans: { small: has({ plan: 'small' }), medium: has({ plan: 'medium' }), large: has({ plan: 'large' }) },
    intervals: { month: has({ interval: 'month' }), year: has({ interval: 'year' }) },
    rssToggle: has({ rss: !selection.rss }),
  };
}

/** "349 €", "4.188 €", "€349", "$1,234.50": whole amounts without decimals. */
export function formatActivationAmount(amount: number, currency: ActivationCurrency, locale: string): string {
  const whole = amount % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

const CARD_BRANDS: Record<string, string> = {
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'eftpos',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

/**
 * Brand or account and the last digits: "Visa •••• 4242", "DE •••• 3000"
 * (Stripe hands out only the IBAN's country and its last four digits),
 * "STRIPE TEST BANK •••• 6789".
 */
export function formatPaymentMethodLabel(method: ActivationPaymentMethod): string {
  let prefix: string | null;
  if (method.type === 'card') {
    const key = (method.brand ?? 'card').toLowerCase();
    prefix = CARD_BRANDS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
  } else {
    prefix = method.type === 'sepa_debit' ? method.country : method.bankName;
  }
  return prefix === null ? `•••• ${method.last4}` : `${prefix} •••• ${method.last4}`;
}

/** "12/30"; null for debits and cards without a known expiry. */
export function formatCardExpiry(method: ActivationPaymentMethod): string | null {
  if (method.type !== 'card' || method.expMonth === null || method.expYear === null) {
    return null;
  }
  return `${String(method.expMonth).padStart(2, '0')}/${String(method.expYear % 100).padStart(2, '0')}`;
}

/**
 * Whether a checkout still has something to activate: a subscription that is
 * not active yet (trialing, paused, past due, ...).
 */
export function canActivate(checkout: ActivationCheckout): boolean {
  return checkout.mode === 'checkout' && checkout.status !== 'active';
}
